/**
 * An error message to show when the user attempts to access something
 * that doesn't exist.
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import {useContext} from "react";
import {useLocation} from "react-router-dom-v5-compat";

import Button from "@khanacademy/wonder-blocks-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {actionStyles} from "@khanacademy/wonder-blocks-styles";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Caption,
    HeadingMedium,
    Title,
} from "@khanacademy/wonder-blocks-typography";
import {AppShellContext} from "@khan/shared-core/context";

import NotFoundBackground from "./internal/not-found-background.tsx";
import ReloadPage from "./reload-page.tsx";

const PATH_ENTRYPOINTS = {
    devadmin: "/devadmin",
    assessments: "/assessments",
    "writing-coach": "/writing-coach",
};

export default function NotFound() {
    const {entrypoint} = useContext(AppShellContext);
    const {pathname} = useLocation();

    // If we're attempting to render a page that doesn't exist inside our
    // entrypoint, then we need to reload the page instead of rendering
    // to force the user to the correct entrypoint.
    if (PATH_ENTRYPOINTS[entrypoint]) {
        if (!pathname.startsWith(PATH_ENTRYPOINTS[entrypoint])) {
            return <ReloadPage />;
        }
    } else if (
        Object.values(PATH_ENTRYPOINTS).some((path) =>
            pathname.startsWith(path),
        )
    ) {
        return <ReloadPage />;
    }

    return (
        <NotFoundBackground>
            <Title>{t`Oops!`}</Title>
            <Strut size={spacing.medium_16} />
            <HeadingMedium tag="div">
                {t`Sorry, the page you were trying to load doesn't exist.`}
            </HeadingMedium>
            <Strut size={spacing.xLarge_32} />
            <Button href="/" style={actionStyles.inverse} kind="secondary">
                {t`Go to Khan Academy homepage`}
            </Button>
            <Strut size={spacing.large_24} />
            <Caption style={styles.reportText}>
                <Trans>
                    If this problem continues and you think something is wrong,
                    please{" "}
                    <Link href="/reportissue?type=Defect" light={true}>
                        {t`send us a report`}
                    </Link>{" "}
                    and we'll look into it.
                </Trans>
            </Caption>
        </NotFoundBackground>
    );
}

const styles = StyleSheet.create({
    reportText: {
        color: color.white64,
    },
});
